import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import AuthProvider, { useAuth } from './context/AuthContext'
import { ThemeProvider } from './components/ThemeProvider'
import { useTheme } from './hooks/useTheme'
import ToasterWithTheme from './components/ToasterWithTheme'
import { ShoppingCartProvider } from './context/ShoppingCartContext'

// Pages
import HomePage from './pages/HomePage'
import ShoppingCartPage from './pages/ShoppingCartPage'
import CheckoutPage from './pages/CheckoutPage'
import LoginPage from './pages/LoginPage'
import PaymentReturnPage from './pages/PaymentReturnPage'
import PaymentCancelledPage from './pages/PaymentCancelledPage'
import ReservationConfirmationPage from './pages/ReservationConfirmationPage'
import ContactPage from './pages/ContactPage'
// Flujo de reservas (anteriormente V2)
import SpaceTypesPageV2 from './pages/SpaceTypesPageV2'
import SpacesByTypePageV2 from './pages/SpacesByTypePageV2'
import SpaceDetailBookingPage from './pages/SpaceDetailBookingPage'
// Admin Pages
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import DefaultDashboard from './pages/admin/DefaultDashboard'
import AdminEspacios from './pages/admin/AdminEspacios'
import AdminTiposEspacios from './pages/admin/AdminTiposEspacios'
import ReservationsAdmin from './pages/admin/ReservationsAdmin'
import CreateReservaManualPage from './pages/admin/CreateReservaManualPage'
import PaymentsAdmin from './pages/admin/PaymentsAdmin'
import DTEsAdmin from './pages/admin/DTEsAdmin'
import CalendarioAdmin from './pages/admin/CalendarioAdmin'
import ConfiguracionAdmin from './pages/admin/ConfiguracionAdmin'
import QRScannerPage from './pages/admin/QRScannerPage'
import QRLocalizadorPage from './pages/admin/QRLocalizadorPage'
import BloqueosAdmin from './pages/admin/BloqueosAdmin'
import ReportsPage from './pages/ReportsPage'
import ControlPage from './pages/ControlPage'
import OcupacionPanoramicaPage from './pages/admin/OcupacionPanoramicaPage'

// Styles
import './App.css'

// Configuración de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

// Componente para página 404
const NotFoundPage = () => {
  const { colors } = useTheme()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900">404</h1>
        <p className="text-xl text-gray-600 mt-4">Página no encontrada</p>
        <a
          href="/"
          style={{
            backgroundColor: colors.primary,
            color: '#FFFFFF'
          }}
          className="mt-6 inline-block px-6 py-3 rounded-lg hover:opacity-90 transition-colors"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  )
}

// Componente para proteger rutas
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ShoppingCartProvider>
        <QueryClientProvider client={queryClient}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="min-h-screen bg-gray-50">
            <Routes>
              {/* Rutas públicas */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/contacto" element={<ContactPage />} />

              {/* Flujo de reservas - Exploración de espacios */}
              <Route path="/reservar" element={<SpaceTypesPageV2 />} />
              <Route path="/reservar/:tipoSlug" element={<SpacesByTypePageV2 />} />
              <Route path="/reservar/:tipoSlug/:espacioId" element={<SpaceDetailBookingPage />} />

              {/* Carrito y checkout */}
              <Route path="/carrito" element={<ShoppingCartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />

              {/* Pagos */}
              <Route path="/payment/return" element={<PaymentReturnPage />} />
              <Route path="/payment/cancelled" element={<PaymentCancelledPage />} />
              <Route path="/confirmacion-reserva" element={<ReservationConfirmationPage />} />

              <Route path="/" element={<HomePage />} />

              {/* Rutas protegidas - Panel de Administración */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                {/* Sub-rutas de administración */}
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="default-dashboard" element={<DefaultDashboard />} />
                <Route path="qr-scanner" element={<QRScannerPage />} />
                <Route path="qr-localizador" element={<QRLocalizadorPage />} />
                <Route path="reservas" element={<ReservationsAdmin />} />
                <Route path="reservas/crear-manual" element={<CreateReservaManualPage />} />
                <Route path="pagos" element={<PaymentsAdmin />} />
                <Route path="dtes" element={<DTEsAdmin />} />
                <Route path="espacios" element={<AdminEspacios />} />
                <Route path="tipos-espacios" element={<AdminTiposEspacios />} />
                <Route path="calendario" element={<CalendarioAdmin />} />
                <Route path="bloqueos" element={<BloqueosAdmin />} />
                <Route path="ocupacion-panoramica" element={<OcupacionPanoramicaPage />} />
                <Route path="reportes" element={<ReportsPage />} />
                <Route path="control" element={<ControlPage />} />
                <Route path="configuracion" element={<ConfiguracionAdmin />} />
              </Route>

              {/* Ruta 404 */}
              <Route path="*" element={<NotFoundPage />} />
              </Routes>

            {/* Notificaciones Toast con colores dinámicos */}
            <ToasterWithTheme />
          </div>
        </Router>

        {/* React Query DevTools */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
        </ShoppingCartProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
